import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import MasterAdmins from './MasterAdmins';
import MasterBowlers from './MasterBowlers';
import MasterScouts from './MasterScouts';
import MasterBookies from './MasterBookies';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface SupportTicket {
  id: number;
  admin_id: number | null;
  bowler_id: number | null;
  created_at: string;
  admin_email: string | null;
  reason: string;
  category: string;
  subcategory: string | null;
  questions: string;
  resolved: boolean;
  admin_name: string | null;
  bowler_name: string | null;
}

interface DashboardStats {
  subscribers: number;
  leagues: number;
  admins: number;
  scouts: number;
  bookies: number;
  annualEarnings: number;
}

type SortField = 'created_at' | 'reason' | 'category';
type SortOrder = 'asc' | 'desc';

const MasterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedTicket, setExpandedTicket] = useState<number | null>(null);
  const [processingTicket, setProcessingTicket] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<'support' | 'admins' | 'bowlers' | 'scouts' | 'bookies'>('support');
  const [stats, setStats] = useState<DashboardStats | null>(null);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const endpoint = showArchived ? '/master/support/archived' : '/master/support/active';
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedTickets = [...tickets].sort((a, b) => {
    let aValue: any, bValue: any;

    if (sortField === 'created_at') {
      aValue = new Date(a.created_at).getTime();
      bValue = new Date(b.created_at).getTime();
    } else {
      aValue = a[sortField]?.toLowerCase() || '';
      bValue = b[sortField]?.toLowerCase() || '';
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleToggleResolved = async (ticketId: number, currentStatus: boolean) => {
    try {
      setProcessingTicket(ticketId);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const endpoint = currentStatus ? 'unresolve' : 'resolve';
      
      const response = await fetch(`${apiUrl}/master/support/${ticketId}/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Refresh tickets
        await fetchTickets();
      }
    } catch (error) {
      console.error('Failed to update ticket:', error);
    } finally {
      setProcessingTicket(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-white">
                    {t('superadmin.dashboard.welcome', { name: user.name })}
                  </h1>
                  <div className="flex items-center space-x-1 bg-gradient-to-r from-pink-400 to-purple-500 px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-semibold">👑 {t('superadmin.dashboard.superAdmin')}</span>
                  </div>
                </div>
                <p className="text-purple-200 text-sm">
                  {t('superadmin.dashboard.masterControl')}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="group flex items-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <span className="text-white font-semibold">{t('common.logout')}</span>
              <svg
                className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <button
            onClick={() => setActiveView('support')}
            className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
              activeView === 'support'
                ? 'bg-purple-600/30 border-purple-400'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
          >
            <div className="text-4xl mb-2">💬</div>
            <h3 className="text-white font-bold text-lg">{t('superadmin.nav.support')}</h3>
          </button>

          <button
            onClick={() => setActiveView('admins')}
            className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
              activeView === 'admins'
                ? 'bg-blue-600/30 border-blue-400'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
          >
            <div className="text-4xl mb-2">👔</div>
            <h3 className="text-white font-bold text-lg">{t('superadmin.nav.admins')}</h3>
          </button>

          <button
            onClick={() => setActiveView('bowlers')}
            className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
              activeView === 'bowlers'
                ? 'bg-green-600/30 border-green-400'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
          >
            <div className="text-4xl mb-2">🎳</div>
            <h3 className="text-white font-bold text-lg">{t('superadmin.nav.bowlers')}</h3>
          </button>

          <button
            onClick={() => setActiveView('scouts')}
            className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
              activeView === 'scouts'
                ? 'bg-yellow-600/30 border-yellow-400'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
          >
            <div className="text-4xl mb-2">🔍</div>
            <h3 className="text-white font-bold text-lg">{t('superadmin.nav.scouts')}</h3>
          </button>

          <button
            onClick={() => setActiveView('bookies')}
            className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
              activeView === 'bookies'
                ? 'bg-blue-200/30 border-blue-400'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
          >
            <div className="text-4xl mb-2">🪙</div>
            
            <h3 className="text-white font-bold text-lg">Bookies</h3>
          </button>
        </div>

        {/* Support Tickets Section */}
        {activeView === 'support' && (
          <>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20"
            style={{
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#6366f1 #18181b'
                }}>
          {/* Header with Toggle */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {showArchived ? t('superadmin.support.archivedTickets') : t('superadmin.support.activeTickets')}
              </h2>
              <p className="text-purple-200">
                {t('superadmin.support.ticketCount', { count: tickets.length })}
              </p>
            </div>
            
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center space-x-2 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400/30 px-6 py-3 rounded-lg transition-all"
            >
              <span className="text-2xl">{showArchived ? '📋' : '🗄️'}</span>
              <span className="text-white font-semibold">
                {showArchived ? t('superadmin.support.viewActive') : t('superadmin.support.viewArchived')}
              </span>
            </button>
          </div>

          {/* Sort Controls */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => handleSort('created_at')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                sortField === 'created_at'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/10 text-purple-200 hover:bg-white/20'
              }`}
            >
              {t('superadmin.support.sortByDate')} {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('reason')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                sortField === 'reason'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/10 text-purple-200 hover:bg-white/20'
              }`}
            >
              {t('superadmin.support.sortByReason')} {sortField === 'reason' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('category')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                sortField === 'category'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/10 text-purple-200 hover:bg-white/20'
              }`}
            >
              {t('superadmin.support.sortByCategory')} {sortField === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          </div>

          {/* Tickets List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
              <p className="text-purple-200 mt-4">{t('common.loading')}</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-6xl">📭</span>
              <p className="text-purple-200 mt-4 text-lg">
                {showArchived ? t('superadmin.support.noArchivedTickets') : t('superadmin.support.noActiveTickets')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all"
                >
                  {/* Ticket Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-purple-300 font-mono text-sm">#{ticket.id}</span>
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-xs font-semibold">
                          {ticket.reason}
                        </span>
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-semibold">
                          {ticket.category}
                        </span>
                        {ticket.subcategory && (
                          <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-semibold">
                            {ticket.subcategory}
                          </span>
                        )}
                      </div>
                      <p className="text-purple-200 text-sm">
                        {t('superadmin.support.submittedBy', {
                          name: ticket.admin_name || ticket.bowler_name || 'Unknown',
                          email: ticket.admin_email || 'N/A'
                        })}
                      </p>
                      <p className="text-purple-300 text-xs mt-1">
                        {formatDate(ticket.created_at)}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                        className="px-4 py-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 rounded-lg transition-all"
                      >
                        {expandedTicket === ticket.id ? t('superadmin.support.hideDetails') : t('superadmin.support.viewDetails')}
                      </button>
                      <button
                        onClick={() => handleToggleResolved(ticket.id, ticket.resolved)}
                        disabled={processingTicket === ticket.id}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all disabled:opacity-50 ${
                          ticket.resolved
                            ? 'bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200'
                            : 'bg-green-600/30 hover:bg-green-600/50 text-green-200'
                        }`}
                      >
                        {processingTicket === ticket.id ? (
                          <span className="inline-block animate-spin">⏳</span>
                        ) : ticket.resolved ? (
                          t('superadmin.support.markUnresolved')
                        ) : (
                          t('superadmin.support.markResolved')
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedTicket === ticket.id && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <h4 className="text-white font-semibold mb-2">{t('superadmin.support.question')}:</h4>
                      <p className="text-purple-100 whitespace-pre-wrap bg-white/5 p-4 rounded-lg">
                        {ticket.questions}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Dashboard Stats - Only show in support view */}
        {activeView === 'support' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-8">
            {/* Annual Earnings Card */}
            <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 backdrop-blur-lg rounded-xl p-6 border-2 border-green-400">
              <h3 className="text-green-200 text-sm mb-2">Annual Earnings</h3>
              <p className="text-white text-3xl font-bold mb-1">
                ${stats.annualEarnings.toLocaleString()}
              </p>
              <p className="text-green-300 text-xs">Before taxes and fees</p>
            </div>

            {/* Subscribers Card */}
            <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur-lg rounded-xl p-6 border-2 border-purple-400">
              <h3 className="text-purple-200 text-sm mb-2">Subscribers</h3>
              <p className="text-white text-3xl font-bold mb-1">{stats.subscribers}</p>
              <p className="text-purple-300 text-xs">Active subscriptions</p>
            </div>

            {/* Leagues Card */}
            <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/30 backdrop-blur-lg rounded-xl p-6 border-2 border-blue-400">
              <h3 className="text-blue-200 text-sm mb-2">Leagues</h3>
              <p className="text-white text-3xl font-bold mb-1">{stats.leagues}</p>
              <p className="text-blue-300 text-xs">Total leagues</p>
            </div>

            {/* Admins Card */}
            <div className="bg-gradient-to-br from-indigo-600/30 to-purple-600/30 backdrop-blur-lg rounded-xl p-6 border-2 border-indigo-400">
              <h3 className="text-indigo-200 text-sm mb-2">Admins</h3>
              <p className="text-white text-3xl font-bold mb-1">{stats.admins}</p>
              <p className="text-indigo-300 text-xs">Total admins</p>
            </div>

            {/* Scouts Card */}
            <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/30 backdrop-blur-lg rounded-xl p-6 border-2 border-yellow-400">
              <h3 className="text-yellow-200 text-sm mb-2">Scouts</h3>
              <p className="text-white text-3xl font-bold mb-1">{stats.scouts}</p>
              <p className="text-yellow-300 text-xs">Total scouts</p>
            </div>

            {/* Bookies Card */}
            <div className="bg-gradient-to-br from-blue-200/30 to-blue-400/30 backdrop-blur-lg rounded-xl p-6 border-2 border-blue-400">
              <h3 className="text-blue-200 text-sm mb-2">Bookies</h3>
              <p className="text-white text-3xl font-bold mb-1">{stats.bookies}</p>
              <p className="text-blue-300 text-xs">Total bookies</p>
            </div>
          </div>
        )}
          </>
        )}

        {/* Admins View */}
        {activeView === 'admins' && <MasterAdmins />}

        {/* Bowlers View */}
        {activeView === 'bowlers' && <MasterBowlers />}

        {/* Scouts View */}
        {activeView === 'scouts' && <MasterScouts />}

        {/* Bookies View */}
        {activeView === 'bookies' && <MasterBookies />}
      </div>
    </div>
  );
};

export default MasterDashboard;
